import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import useAuthStore, { User } from './useAuthStore';

export type FriendStatus = 'pending' | 'accepted' | 'declined' | 'blocked';

export interface Friend {
    id: string;
    userId: string;
    friendId: string;
    status: FriendStatus;
    createdAt: string;
    updatedAt: string;
}

export interface FriendWithProfile extends Friend {
    profile: User;
    lastActivity?: string;
}

// API Response Types
interface IncomingFriendRequestAPIResponse {
  request_id: string;
  sender_user_id: string;
  sender_username: string | null;
  sender_full_name: string | null;
  sender_avatar_key: string | null;
  status: string;
  created_at: number; // timestamp
}

interface OutgoingFriendRequestAPIResponse {
  request_id: string;
  receiver_user_id: string;
  receiver_username: string | null;
  receiver_full_name: string | null;
  receiver_avatar_key: string | null;
  status: string;
  created_at: number; // timestamp
}

// Store View Types
export interface PendingRequestView {
  id: string; // request_id
  sender: {
    userId: string;
    username: string | null;
    fullName: string | null;
    avatarKey: string | null;
  };
  status: FriendStatus;
  createdAt: string; // ISO string
}

export interface SentRequestView {
  id: string; // request_id
  receiver: {
    userId: string;
    username: string | null;
    fullName: string | null;
    avatarKey: string | null;
  };
  status: FriendStatus;
  createdAt: string; // ISO string
}


interface FriendsState {
    friends: FriendWithProfile[];
    pendingRequests: PendingRequestView[];
    sentRequests: SentRequestView[];
    blockedUsers: Friend[];
    loading: boolean;
    error: string | null;

    // Friend actions
    sendFriendRequest: (recipient: { userId?: string; email?: string; username?: string }) => Promise<{ success: boolean; error?: string }>;
    acceptFriendRequest: (requestId: string) => Promise<boolean>;
    declineFriendRequest: (requestId: string) => Promise<boolean>;
    cancelFriendRequest: (requestId: string) => Promise<boolean>;
    removeFriend: (friendId: string) => Promise<boolean>;
    blockUser: (userId: string) => Promise<boolean>;
    unblockUser: (userId: string) => Promise<boolean>;

    // Friend data operations
    getFriendRequests: () => Promise<void>;
    getFriends: () => Promise<void>;

    // Helper methods
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    clearFriendsState: () => void;
}

const useFriendsStore = create<FriendsState>()(
    persist(
        (set, get) => ({
            friends: [],
            pendingRequests: [],
            sentRequests: [],
            blockedUsers: [],
            loading: false,
            error: null,

            sendFriendRequest: async (recipient) => {
                // set({ loading: true, error: null });
                try {
                    const bodyPayload: { receiverUserId?: string; receiverUserEmail?: string; receiverUsername?: string } = {};
                    if (recipient.userId) {
                        bodyPayload.receiverUserId = recipient.userId;
                    } else if (recipient.email) {
                        bodyPayload.receiverUserEmail = recipient.email;
                    } else if (recipient.username) {
                        bodyPayload.receiverUsername = recipient.username;
                    } else {
                        throw new Error("Recipient user ID, email, or username is required.");
                    }

                    const response = await fetch('/api/me/friend-requests', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include', // Send cookies
                        body: JSON.stringify(bodyPayload),
                    });
                    const data = await response.json();
                    if (!response.ok) { // Check response.ok first
                        // Try to parse error from data if available, otherwise use generic message
                        const errorMessage = data?.error || data?.message || `Failed to send friend request. Status: ${response.status}`;
                        throw new Error(errorMessage);
                    }
                    // data.success might not exist if the backend returns errors differently now.
                    // If response.ok is true, and the backend follows typical REST patterns for success (2xx),
                    // we might not need data.success explicitly if the error cases are handled by !response.ok
                    // However, if the backend returns 200 OK but with { success: false, message: "..." }, then we need it.
                    // For now, let's assume if response.ok, it's a success unless data explicitly says otherwise.
                    if (data.success === false) { // Explicit check for backend indicating operational failure despite HTTP 200
                        throw new Error(data.message || data.error || 'Failed to send friend request.');
                    }
                    // Optimistically add to sentRequests or refetch
                    get().getFriendRequests(); // Refetch requests
                    // set({ loading: false });
                    return { success: true };
                } catch (error: unknown) {
                    const errorMessage = (error as Error).message || 'Failed to send friend request.';
                    console.error('Error sending friend request:', error);
                    // set({ loading: false, error: errorMessage }); // Keep setting global error for other potential consumers
                    return { success: false, error: errorMessage };
                }
            },

            acceptFriendRequest: async (requestId) => {
                set({ loading: true, error: null });
                try {
                    const response = await fetch(`/api/me/friend-requests/${requestId}`, {
                        method: 'PUT',
                        headers: {},
                        credentials: 'include', // Send cookies
                    });
                    const data = await response.json();
                    if (!response.ok || !data.success) {
                        throw new Error(data.message || 'Failed to accept friend request.');
                    }
                    // Refetch friends and friend requests
                    get().getFriends();
                    get().getFriendRequests();
                    set({ loading: false });
                    return true;
                } catch (error: unknown) {
                    console.error('Error accepting friend request:', error);
                    set({ loading: false, error: (error as Error).message || 'Failed to accept friend request.' });
                    return false;
                }
            },

            declineFriendRequest: async (requestId) => { // Also used for rejecting
                set({ loading: true, error: null });
                try {
                    const response = await fetch(`/api/me/friend-requests/${requestId}`, {
                        method: 'DELETE',
                        headers: {},
                        credentials: 'include', // Send cookies
                    });
                    const data = await response.json();
                    if (!response.ok || !data.success) {
                        throw new Error(data.message || 'Failed to decline friend request.');
                    }
                    get().getFriendRequests(); // Refetch requests
                    set({ loading: false });
                    return true;
                } catch (error: unknown) {
                    console.error('Error declining friend request:', error);
                    set({ loading: false, error: (error as Error).message || 'Failed to decline friend request.' });
                    return false;
                }
            },

            cancelFriendRequest: async (requestId) => { // Same as decline for now, as DELETE handles both based on who calls
                set({ loading: true, error: null });
                try {
                    const response = await fetch(`/api/me/friend-requests/${requestId}`, {
                        method: 'DELETE',
                        headers: {},
                        credentials: 'include', // Send cookies
                    });
                    const data = await response.json();
                    if (!response.ok || !data.success) {
                        throw new Error(data.message || 'Failed to cancel friend request.');
                    }
                    get().getFriendRequests(); // Refetch requests
                    set({ loading: false });
                    return true;
                } catch (error: unknown) {
                    console.error('Error cancelling friend request:', error);
                    set({ loading: false, error: (error as Error).message || 'Failed to cancel friend request.' });
                    return false;
                }
            },

            removeFriend: async (friendUserId) => {
                set({ loading: true, error: null });
                try {
                    const response = await fetch(`/api/me/friends/${friendUserId}`, {
                        method: 'DELETE',
                        headers: {},
                        credentials: 'include', // Send cookies
                    });
                    const data = await response.json();
                    if (!response.ok || !data.success) {
                        throw new Error(data.message || 'Failed to remove friend.');
                    }
                    get().getFriends(); // Refetch friends
                    set({ loading: false });
                    return true;
                } catch (error: unknown) {
                    console.error('Error removing friend:', error);
                    set({ loading: false, error: (error as Error).message || 'Failed to remove friend.' });
                    return false;
                }
            },

            blockUser: async (userId) => {
                const authStore = useAuthStore.getState();
                const { user } = authStore;

                if (!user) {
                    set({ error: 'You must be logged in to block users' });
                    return false;
                }

                set({ loading: true, error: null });

                try {
                    // Simulate API call delay
                    await new Promise(resolve => setTimeout(resolve, 500));

                    // Create block record
                    const blockRecord: Friend = {
                        id: `block_${Date.now()}`,
                        userId: user.id,
                        friendId: userId,
                        status: 'blocked',
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                    };

                    set(state => {
                        // Remove from friends if exists
                        const updatedFriends = state.friends.filter(f =>
                            !(f.friendId === userId || f.userId === userId)
                        );

                        // Remove from pending requests if exists (user being blocked is the sender)
                        const updatedPending = state.pendingRequests.filter(r =>
                            r.sender.userId !== userId
                        );

                        // Remove from sent requests if exists (user being blocked is the receiver)
                        const updatedSent = state.sentRequests.filter(r =>
                            r.receiver.userId !== userId
                        );

                        return {
                            friends: updatedFriends,
                            pendingRequests: updatedPending,
                            sentRequests: updatedSent,
                            blockedUsers: [...state.blockedUsers, blockRecord],
                            loading: false,
                        };
                    });

                    return true;
                } catch {
                    set({
                        loading: false,
                        error: 'Failed to block user. Please try again.'
                    });
                    return false;
                }
            },

            unblockUser: async (userId) => {
                set({ loading: true, error: null });

                try {
                    // Simulate API call delay
                    await new Promise(resolve => setTimeout(resolve, 500));

                    set(state => ({
                        blockedUsers: state.blockedUsers.filter(b => b.friendId !== userId),
                        loading: false,
                    }));

                    return true;
                } catch {
                    set({
                        loading: false,
                        error: 'Failed to unblock user. Please try again.'
                    });
                    return false;
                }
            },

            getFriendRequests: async () => {
                set({ loading: true, error: null });
                try {
                    const [incomingRes, outgoingRes] = await Promise.all([
                        fetch('/api/me/friend-requests?type=incoming', {
                            headers: {
                            },
                            credentials: 'include', // Send cookies
                        }),
                        fetch('/api/me/friend-requests?type=outgoing', {
                            headers: {
                            },
                            credentials: 'include', // Send cookies
                        }),
                    ]);

                    if (!incomingRes.ok) {
                        const errorText = await incomingRes.text();
                        throw new Error(`Failed to fetch incoming friend requests: ${incomingRes.status} ${errorText}`);
                    }
                    const incomingApiData: IncomingFriendRequestAPIResponse[] = await incomingRes.json();

                    if (!outgoingRes.ok) {
                        const errorText = await outgoingRes.text();
                        throw new Error(`Failed to fetch outgoing friend requests: ${outgoingRes.status} ${errorText}`);
                    }
                    const outgoingApiData: OutgoingFriendRequestAPIResponse[] = await outgoingRes.json();

                    const pending: PendingRequestView[] = incomingApiData.map((req) => ({
                        id: req.request_id,
                        sender: {
                            userId: req.sender_user_id,
                            username: req.sender_username,
                            fullName: req.sender_full_name,
                            avatarKey: req.sender_avatar_key,
                        },
                        status: req.status as FriendStatus,
                        createdAt: new Date(req.created_at).toISOString(),
                    }));

                    const sent: SentRequestView[] = outgoingApiData.map((req) => ({
                        id: req.request_id,
                        receiver: {
                            userId: req.receiver_user_id,
                            username: req.receiver_username,
                            fullName: req.receiver_full_name,
                            avatarKey: req.receiver_avatar_key,
                        },
                        status: req.status as FriendStatus,
                        createdAt: new Date(req.created_at).toISOString(),
                    }));

                    set({ pendingRequests: pending, sentRequests: sent, loading: false });
                } catch (error: unknown) {
                    console.error('Error fetching friend requests:', error);
                    set({ loading: false, error: (error as Error).message || 'Failed to fetch friend requests.' });
                }
            },

            getFriends: async () => {
                set({ loading: true, error: null });
                try {
                    const response = await fetch('/api/me/friends', {
                        headers: {},
                        credentials: 'include', // Send cookies
                    });
                    const data = await response.json();

                    if (!response.ok || !data.success) {
                        throw new Error(data.message || 'Failed to fetch friends.');
                    }

                    const friendsWithProfiles: FriendWithProfile[] = data.friends.map((f) => ({
                        id: f.userId, // Use friend's userId as the primary ID for the FriendWithProfile entry
                        userId: useAuthStore.getState().user?.id || '', // Current user
                        friendId: f.userId, // The actual ID of the friend
                        status: 'accepted', // Assumed from /api/me/friends
                        createdAt: new Date(f.establishedAt * 1000).toISOString(),
                        updatedAt: new Date(f.establishedAt * 1000).toISOString(), // Placeholder
                        profile: { // Map backend friend details to User profile structure
                            id: f.userId,
                            username: f.username,
                            email: '', // Not typically returned in friends list for privacy
                            firstName: f.fullName?.split(' ')[0] || '',
                            lastName: f.fullName?.split(' ').slice(1).join(' ') || '',
                            avatar: f.avatarUrl,
                            profilePictureUrl: f.avatarUrl,
                            role: 'user', // Assume 'user' role
                            createdAt: '', // Not available from this endpoint directly
                        },
                        lastActivity: 'Online', // Placeholder
                    }));
                    set({ friends: friendsWithProfiles, loading: false });
                } catch (error: unknown) {
                    console.error('Error fetching friends:', error);
                    set({ loading: false, error: (error as Error).message || 'Failed to fetch friends.' });
                }
            },

            setLoading: (loading) => set({ loading }),
            setError: (error) => set({ error }),
            clearFriendsState: () => set({
                friends: [],
                pendingRequests: [],
                sentRequests: [],
                blockedUsers: [],
                error: null
            })
        }),
        {
            name: 'syncreos-friends-storage',
            // Only persist these fields in localStorage
            partialize: (state) => ({
                friends: state.friends,
                blockedUsers: state.blockedUsers
            })
        }
    )
);

export default useFriendsStore; 